import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

export default function RWD_Videos() {
  return (
    <div className="tutorial-container">
      {/* Hero Section */}
      <div
        style={{
          background: "linear-gradient(135deg, #f97316, #ea580c)",
          color: "white",
          padding: "2rem",
          borderRadius: "16px",
          marginBottom: "2rem",
          textAlign: "center",
        }}
      >
        <h1 style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
          🎬 Responsive Videos (RWD)
        </h1>
        <p style={{ fontSize: "1.2rem", marginTop: "1rem" }}>
          Make videos resize automatically to fit different screen sizes while
          maintaining their aspect ratio.
        </p>
        <img
          src="https://www.w3schools.com/html/img_html5_video.png"
          alt="Responsive Video Example"
          style={{
            maxWidth: "500px",
            margin: "1.5rem auto 0",
            borderRadius: "12px",
            boxShadow: "0 6px 20px rgba(0,0,0,0.2)",
          }}
        />
      </div>

      {/* Intro */}
      <p className="tutorial-intro">
        Responsive videos are important for modern web design. By wrapping
        videos in a container and using CSS properties such as{" "}
        <code>max-width: 100%</code> and <code>height: auto</code>, you can
        ensure videos scale properly across devices.
      </p>

      {/* Key Points */}
      <div
        style={{
          background: "linear-gradient(135deg, #fcd34d, #fbbf24)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#78350f" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            Wrap videos in a responsive container to maintain aspect ratio.
          </li>
          <li>
            Use <code>width: 100%</code> and <code>height: auto</code> for
            scaling.
          </li>
          <li>
            Aspect ratio can also be preserved using <code>padding-top</code>{" "}
            trick (e.g., 56.25% for 16:9).
          </li>
          <li>
            HTML <code>&lt;video&gt;</code> tag supports <code>controls</code>,{" "}
            <code>autoplay</code>, and <code>muted</code>.
          </li>
          <li>Combine with media queries for advanced responsive behavior.</li>
        </ul>
      </div>

      {/* Example 1: Basic Responsive Video */}
      <h2 className="section-title">1️⃣ Basic Responsive Video</h2>
      <CodeCopyBlock
        code={`video {
  max-width: 100%;
  height: auto;
}`}
      />
      <p>
        ✅ Video scales to fit the container while maintaining aspect ratio.
      </p>
      <video
        controls
        src="https://www.w3schools.com/html/mov_bbb.mp4"
        style={{
          maxWidth: "100%",
          height: "auto",
          borderRadius: "8px",
          marginBottom: "1.5rem",
        }}
      ></video>

      {/* Example 2: Video in Responsive Container */}
      <h2 className="section-title">2️⃣ Video in Responsive Container</h2>
      <CodeCopyBlock
        code={`.video-container {
  position: relative;
  width: 100%;
  padding-top: 56.25%; /* 16:9 aspect ratio */
}

.video-container video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}`}
      />
      <p>✅ Maintains aspect ratio using a container with padding-top trick.</p>
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingTop: "56.25%",
          marginBottom: "1.5rem",
        }}
      >
        <video
          controls
          src="https://www.w3schools.com/html/mov_bbb.mp4"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            borderRadius: "8px",
          }}
        ></video>
      </div>

      {/* Example 3: Video with Media Queries */}
      <h2 className="section-title">3️⃣ Media Query for Video Width</h2>
      <CodeCopyBlock
        code={`.video-responsive {
  width: 100%;
}

@media (min-width: 768px) {
  .video-responsive {
    width: 75%;
  }
}

@media (min-width: 1200px) {
  .video-responsive {
    width: 50%;
  }
}`}
      />
      <p>✅ Adjusts video width depending on screen size for more control.</p>
      <video
        controls
        className="video-responsive"
        src="https://www.w3schools.com/html/mov_bbb.mp4"
        style={{
          width: "100%",
          height: "auto",
          borderRadius: "8px",
          marginBottom: "2rem",
        }}
      ></video>

      {/* Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #d9f99d, #bef264)",
          padding: "1.2rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginBottom: "2rem",
        }}
      >
        <h2 style={{ color: "#365314" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice embedding videos using <code>&lt;video&gt;</code> and
            scaling them with CSS.
          </li>
          <li>
            Use containers with <code>padding-top</code> to maintain aspect
            ratios for different devices.
          </li>
          <li>
            Combine media queries with video containers to adjust layout on
            various screen sizes.
          </li>
          <li>
            Experiment with autoplay, muted, and loop attributes for different
            UX scenarios.
          </li>
          <li>
            Test videos on desktop, tablet, and mobile to ensure proper
            responsiveness.
          </li>
        </ul>
      </div>
    </div>
  );
}
